<template>
  <div class="country-city-selector">
    <div class="selector-row">
      <div class="p-field" v-if="showCountry">
        <span class="p-float-label">
          <Dropdown
            id="country"
            v-model="selectedCountry"
            :options="countries"
            optionLabel="description"
            :placeholder="$t('select_country')"
            :loading="loadingCountries"
            @change="onCountryChange"
            class="w-full"
            filter
            showClear
          >
            <template #value="slotProps">
              <div class="country-item country-item-value d-flex align-items-center" v-if="slotProps.value">
                <i v-if="slotProps.value.iso2" :class="`fi fi-${slotProps.value.iso2.toLowerCase()}`"></i>
                <div class="ml-2">{{ slotProps.value.description }}</div>
              </div>
            </template>
            <template #option="slotProps">
              <div class="country-item d-flex align-items-center" v-if="slotProps.option">
                <i v-if="slotProps.option.iso2" :class="`fi fi-${slotProps.option.iso2.toLowerCase()}`"></i>
                <div class="ml-2">{{ slotProps.option.description }}</div>
              </div>
            </template>
          </Dropdown>
          <label for="country">{{ $t('country') }}</label>
        </span>
      </div>
      <div class="p-field" v-if="showCity">
        <span class="p-float-label">
          <Dropdown
            id="city"
            v-model="selectedCity"
            :options="cities"
            optionLabel="description"
            :placeholder="$t('select_city')"
            :loading="loadingCities"
            :disabled="!selectedCountry"
            class="w-full"
            filter
            showClear
          />
          <label for="city">{{ $t('city') }}</label>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { get, post } from '@/api/Services'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      country: null,
      city: null
    })
  },
  showCountry: {
    type: Boolean,
    default: true
  },
  showCity: {
    type: Boolean,
    default: true
  },
  disableAutodetect: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const countries = ref([])
const cities = ref([])
const selectedCountry = ref(null)
const selectedCity = ref(null)
const loadingCountries = ref(false)
const loadingCities = ref(false)

const fetchCountries = async () => {
  loadingCountries.value = true
  try {
    const response = await post('/Commons/Executer', { sqlId: 71 })
    countries.value = response
    
    // If there's a value in the props, select the country
    if (props.modelValue?.country) {
      // Check if we have a full country object or just an ID
      if (props.modelValue.country.description) {
        selectedCountry.value = props.modelValue.country
      } else {
        // Only have ID, find the complete object
        selectedCountry.value = countries.value.find(
          country => country.id === props.modelValue.country.id
        )
      }
      
      // If we have a country, fetch cities next
      if (selectedCountry.value && props.showCity) {
        fetchCities(selectedCountry.value.id)
      }
    } else if (!props.disableAutodetect) {
      // No country is selected yet, try to detect location automatically
      detectLocation()
    }
  } catch (error) {
    console.error('Error fetching countries:', error)
  } finally {
    loadingCountries.value = false
  }
}

const fetchCities = async (countryId) => {
  if (!countryId) {
    cities.value = []
    selectedCity.value = null
    return
  }

  loadingCities.value = true
  try {
    const response = await get(`/Cities/${countryId}`)
    cities.value = response
    
    // If there's a city in the props, select it
    if (props.modelValue?.city) {
      // Check if we have a full city object or just an ID
      if (props.modelValue.city.description) {
        selectedCity.value = props.modelValue.city
      } else {
        selectedCity.value = cities.value.find(
          city => city.id === props.modelValue.city.id
        )
      }
    }
  } catch (error) {
    console.error(`Error fetching cities for country ${countryId}:`, error)
  } finally {
    loadingCities.value = false
  }
}

const onCountryChange = () => {
  selectedCity.value = null
  if (selectedCountry.value && props.showCity) {
    fetchCities(selectedCountry.value.id)
  } else {
    cities.value = []
  }
  updateModelValue()
}

const updateModelValue = () => {
  emit('update:modelValue', {
    country: selectedCountry.value,
    city: selectedCity.value
  })
}

// Detect user's location quietly
const detectLocation = async () => {
  if (!navigator.geolocation) {
    return // Silently fail if geolocation not supported
  }
  
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      })
    })
    
    const { latitude, longitude } = position.coords
    await reverseGeocode(latitude, longitude)
  } catch (error) {
    // Silently fail if user denies permission or other error
    console.log('Location detection not available', error)
  }
}

// Get country and city from coordinates
const reverseGeocode = async (latitude, longitude) => {
  try {
    // Using OpenStreetMap Nominatim for reverse geocoding
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`)
    const data = await response.json()
    
    if (data && data.address) {
      // Get country code and city name
      const countryCode = data.address.country_code
      const cityName = data.address.city || data.address.town || data.address.village || data.address.county
      
      // Find country in our list based on ISO code
      if (countryCode && countries.value.length > 0) {
        const country = countries.value.find(c => c.iso2?.toLowerCase() === countryCode.toLowerCase())
        
        if (country) {
          selectedCountry.value = country
          await fetchCities(country.id)
          
          // Try to find city by name
          if (cityName && cities.value.length > 0) {
            const city = cities.value.find(c => 
              c.description.toLowerCase().includes(cityName.toLowerCase()) ||
              cityName.toLowerCase().includes(c.description.toLowerCase())
            )
            
            if (city) {
              selectedCity.value = city
            }
          }
          
          updateModelValue()
        }
      }
    }
  } catch (error) {
    console.error('Error reverse geocoding:', error)
  }
}

// Watch for changes in the selected city
watch(selectedCity, () => {
  updateModelValue()
})

// Watch for changes in the props
watch(() => props.modelValue, (newValue) => {
  if (newValue?.country && (!selectedCountry.value || newValue.country.id !== selectedCountry.value.id)) {
    selectedCountry.value = newValue.country
    if (props.showCity) {
      fetchCities(newValue.country.id)
    }
  }
  
  if (newValue?.city && (!selectedCity.value || newValue.city.id !== selectedCity.value.id)) {
    selectedCity.value = newValue.city
  }
}, { deep: true })

// Only fetch countries if we're showing the country dropdown
onMounted(() => {
  if (props.showCountry) {
    fetchCountries()
  }
})

// Watch for changes to showCountry prop
watch(() => props.showCountry, (newValue) => {
  if (newValue && countries.value.length === 0) {
    fetchCountries()
  }
})

// Watch for changes to showCity prop to fetch cities if needed
watch(() => props.showCity, (newValue) => {
  if (newValue && selectedCountry.value && cities.value.length === 0) {
    fetchCities(selectedCountry.value.id)
  }
})
</script>

<style scoped>
.country-city-selector {
  width: 100%;
  max-height: 42px;
}

.selector-row {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  width: 100%;
  align-items: center;
}

.p-field {
  flex: 1;
}

.p-field .p-float-label {
  display: block;
  width: 100%;
  height: 100%;
}

:deep(.p-dropdown) {
  width: 100% !important;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  transition: all 0.25s;
  height: 100%;
}

:deep(.p-dropdown:hover) {
  border-color: #015cac;
}

:deep(.p-dropdown:focus),
:deep(.p-dropdown.p-focus) {
  border-color: #015cac;
  box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
}

:deep(.p-dropdown-label) {
  padding: 0.75rem;
  display: flex;
  align-items: center;
}

.p-float-label label {
  color: #6c757d;
}

:deep(.p-float-label input:focus ~ label),
:deep(.p-float-label .p-inputwrapper-focus ~ label) {
  color: #015cac;
}

/* Flag styles */
.country-item {
  display: flex;
  align-items: center;
  gap: 2px;
}

.country-item i {
  width: 1.5rem;
  height: 1rem;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.2);
}
</style> 